import {useCallback, useEffect, useRef, useState} from 'react';
import {Skeleton, Spin, Tag} from 'antd';
import {ClockCircleOutlined} from '@ant-design/icons';
import SingleTicketView from '../ticketView/SingleTicketView';
import styles from '../AiHelpDesk.module.scss';
import {shortenTicketId} from '../utils/shortenTicketId';
import {TicketUserAvatar} from './TicketUserAvatar';
import {AwaitingReplyBadge} from './AwaitingReplyBadge';
import {CsatStars, csatTier} from './CsatStars';

const STATUS_COLORS = {
  GUIDED_FLOW: 'blue',
  AI_ACTIVE: 'purple',
  HUMAN_ACTIVE: 'orange',
  ESCALATED: 'red',
  RESOLVED: 'green',
  ABANDONED: 'default',
};

const SCROLL_THRESHOLD_PX = 80;

const getSentimentClass = sentiment => {
  switch (sentiment) {
    case 'positive':
      return styles.green;
    case 'negative':
      return styles.red;
    default:
      return styles.amber;
  }
};

export const TicketsChatView = ({
  tickets,
  loading,
  emptyMessage,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
}) => {
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    if (!tickets.length) {
      setSelectedTicketId(null);
      return;
    }
    if (!tickets.some(ticket => ticket.id === selectedTicketId)) {
      setSelectedTicketId(tickets[0].id);
    }
  }, [loading, selectedTicketId, tickets]);

  const maybeLoadMore = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    if (!hasMore || loadingMore || loading) return;
    if (!onLoadMore) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom <= SCROLL_THRESHOLD_PX) {
      onLoadMore();
    }
  }, [hasMore, loadingMore, loading, onLoadMore]);

  useEffect(() => {
    if (loading) return;
    const el = listRef.current;
    if (!el) return;
    if (!hasMore || loadingMore) return;
    if (el.scrollHeight <= el.clientHeight) {
      maybeLoadMore();
    }
  }, [tickets, loading, hasMore, loadingMore, maybeLoadMore]);

  const handleScroll = () => {
    maybeLoadMore();
  };

  if (loading && !tickets.length) {
    return (
      <div className={styles.chatShell}>
        <div className={styles.chatListPanel}>
          {[...Array(8)].map((_, index) => (
            <div className={styles.chatListSkeleton} key={index}>
              <Skeleton.Avatar active size="large" shape="circle" />
              <div>
                <Skeleton.Input active size="small" style={{width: 140}} />
                <Skeleton.Input active size="small" style={{width: 220}} />
              </div>
            </div>
          ))}
        </div>
        <div className={styles.chatDetailPanel}>
          <Skeleton active paragraph={{rows: 8}} />
        </div>
      </div>
    );
  }

  if (!tickets.length) {
    return (
      <div className={styles.emptyState}>
        {emptyMessage || 'No tickets found.'}
      </div>
    );
  }

  return (
    <div className={styles.chatShell}>
      <div
        className={styles.chatListPanel}
        ref={listRef}
        onScroll={handleScroll}>
        {tickets.map(ticket => (
          <button
            type="button"
            key={ticket.id}
            className={`${styles.chatTicketItem} ${
              selectedTicketId === ticket.id ? styles.activeChatTicket : ''
            } ${
              csatTier(ticket.csatScore) === 'low' ? styles.lowCsatChatItem : ''
            }`}
            onClick={() => setSelectedTicketId(ticket.id)}>
            <TicketUserAvatar
              imageUrl={ticket.userImageUrl}
              initials={ticket.userInitials}
              size="lg"
              style={{width: 44, height: 44, flex: '0 0 44px'}}
            />
            <span className={styles.chatTicketContent}>
              <span className={styles.chatTicketTopline}>
                <span className={styles.chatTicketName}>
                  {ticket.userDisplayName}
                </span>
                <span className={styles.chatTicketTime}>
                  <ClockCircleOutlined /> {ticket.timeElapsed}
                </span>
              </span>
              <span className={styles.chatTicketSubline}>
                {ticket.userSecondaryText}
              </span>
              <AwaitingReplyBadge awaiting={ticket.awaitingReply} />
              <span className={styles.chatTicketId} title={ticket.id}>
                Ticket: {shortenTicketId(ticket.id)}
              </span>
              <span className={styles.chatTicketMeta}>
                <span
                  className={`${styles.chatSentimentDot} ${getSentimentClass(
                    ticket.sentiment,
                  )}`}
                />
                <span className={styles.chatTicketIntent}>
                  {ticket.intentCategory}
                </span>
              </span>
              {ticket.lastMessage ? (
                <span className={styles.chatLatestBubble}>
                  {ticket.lastMessageSender ? (
                    <span className={styles.chatLatestSender}>
                      {ticket.lastMessageSender}
                    </span>
                  ) : null}
                  <span className={styles.chatLatestText}>
                    {ticket.lastMessage}
                  </span>
                </span>
              ) : null}
              <span className={styles.chatTicketFooter}>
                <Tag color={STATUS_COLORS[ticket.status] || 'default'}>
                  {ticket.status?.replace('_', ' ')}
                </Tag>
                {ticket.csatScore !== null && ticket.csatScore !== undefined ? (
                  <CsatStars
                    score={ticket.csatScore}
                    size="sm"
                    showNumeric={false}
                  />
                ) : null}
                {ticket.agentAssigned ? (
                  <span className={styles.chatAgent}>
                    {ticket.agentAssigned}
                  </span>
                ) : null}
              </span>
            </span>
          </button>
        ))}
        {loadingMore ? (
          <div className={styles.chatListLoadMore}>
            <Spin size="small" />
          </div>
        ) : null}
        {!hasMore && !loadingMore ? (
          <div className={styles.chatListEnd}>No more tickets</div>
        ) : null}
      </div>
      <div className={styles.chatDetailPanel}>
        {selectedTicketId ? (
          <SingleTicketView ticketId={selectedTicketId} embedded />
        ) : (
          <div className={styles.chatPlaceholder}>
            Select ticket to view chat.
          </div>
        )}
      </div>
    </div>
  );
};
